'use client';

import {
  Heading,
  NavigationSidebar,
  ProfileImage,
  useNavbarHidden
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { profile as profileSchema } from '@stereoagency/sanity-schema';
import flush from 'just-flush';
import { formatProfileName, sanityImageAsProps } from '@/utils';

const profileQuery = profileSchema.schema.builder
  .pick([
    '_id',
    'image',
    'name',
    'firstName',
    'email',
    'phone',
    'website',
    'address',
    'socials',
    'pubmedQuery',
    'pubmedArticles'
  ])
  .first()
  .use();

type Props = {
  activeId?: string | null;
  profile: (typeof profileQuery)[1];
  hasPublications?: boolean;
  hasBioContent?: boolean;
  hasResearchContent?: boolean;
  hasTeamContent?: boolean;
  hasNews?: boolean;
  hasJobs?: boolean;
  accentColor?: string;
};

export default function GroupOrPlatformSidebar({
  activeId,
  profile,
  hasPublications,
  hasBioContent,
  hasResearchContent,
  hasTeamContent,
  hasNews,
  hasJobs,
  accentColor
}: Props) {
  const { t } = useTranslation('research-group-or-platform');
  const navbarHidden = useNavbarHidden();
  const { email } = profile

  const sidebarItems = flush([
    { label: t('navigation.about'), href: '#about' },
    hasResearchContent
      ? { label: t('navigation.research'), href: '#research' }
      : null,
    hasBioContent ? { label: t('navigation.bio'), href: '#bio' } : null,
    hasTeamContent ? { label: t('navigation.team'), href: '#team' } : null,
    hasPublications
      ? { label: t('navigation.publications'), href: '#publications' }
      : null,
    hasNews ? { label: t('navigation.news'), href: '#news' } : null,
    hasJobs ? { label: t('navigation.jobs'), href: '#jobs' } : null
  ]);

  const activeIndex =
    activeId === null
      ? 0
      : sidebarItems.findIndex((item) => item.href === `#${activeId}`);

  return (
    <>
      {/* sticky side profile */}
      {!!(profile?.image?.asset._ref && profile.name) && (
        <div
          className="sticky left-0 col-start-1 row-start-1 transition-all"
          style={{
            top: navbarHidden ? '2rem' : '9rem'
          }}
        >
          <Heading as="h4" variant="h4" className="text-center p-3">
            {formatProfileName(profile)}
          </Heading>
          <div className=" mx-auto w-[7rem] h-auto ">
            <ProfileImage
              image={sanityImageAsProps(profile.image)}
              color={accentColor}
            />
          </div>
        </div>
      )}

      <NavigationSidebar
        className="col-start-1 row-start-1 sticky left-0 transition-all"
        style={{
          top: navbarHidden ? '14.5rem' : '21.5rem'
        }}
        activeIndex={activeIndex}
        items={sidebarItems}
        action={{
          href: email ? `mailto:${email}` : '#',
          label: t('global:contact'),
          onClick() {
            if (!email) {
              // just scroll to top - which contains contact information
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          }
        }}
      />
    </>
  );
}
